<template>
  <div class="m7-dial" :style="{ '--m7-dial-size': `${size}px` }">
    <svg class="m7-dial__ring" viewBox="0 0 200 200" aria-hidden="true">
      <line
        v-for="tick in ticks"
        :key="tick.index"
        class="m7-dial__tick"
        :class="{ 'is-active': tick.active, 'is-cursor': tick.cursor }"
        x1="100"
        :y1="tick.cursor ? 8 : 12"
        x2="100"
        y2="24"
        :transform="`rotate(${tick.angle} 100 100)`"
      />
    </svg>

    <button
      ref="knob"
      type="button"
      class="m7-dial__knob"
      :aria-label="tapLabel"
      @pointerdown="onPointerDown"
      @keydown.left.prevent="emit('update:modelValue', modelValue - 1)"
      @keydown.right.prevent="emit('update:modelValue', modelValue + 1)"
      @keydown.down.prevent="emit('update:modelValue', modelValue - 5)"
      @keydown.up.prevent="emit('update:modelValue', modelValue + 5)"
    >
      <span class="m7-dial__tap" :class="{ 'is-tapping': tapping }">{{ tapLabel }}</span>
    </button>

    <span class="m7-dial__bound m7-dial__bound--min">{{ min }}</span>
    <span class="m7-dial__bound m7-dial__bound--max">{{ max }}</span>
  </div>
</template>

<script setup>
const props = defineProps({
  modelValue: { type: Number, required: true },
  min: { type: Number, default: 20 },
  max: { type: Number, default: 300 },
  size: { type: Number, default: 220 },
  tapLabel: { type: String, default: 'TAP' },
  tapping: { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue', 'tap'])

const TICK_COUNT = 57
/** Degrees of ring covered by ticks; the gap sits at the bottom. */
const SWEEP = 280
/** Rotation needed for one BPM step. Low enough to stay controllable on a phone. */
const DEGREES_PER_BPM = 2.2
/** Movement under this many px (and quick enough) counts as a tap, not a drag. */
const TAP_SLOP_PX = 8
const TAP_MAX_MS = 400

const knob = ref(null)

const progress = computed(() => {
  const span = props.max - props.min
  if (span <= 0) return 0
  return Math.min(Math.max((props.modelValue - props.min) / span, 0), 1)
})

const ticks = computed(() => {
  const cursorIndex = Math.round(progress.value * (TICK_COUNT - 1))
  return Array.from({ length: TICK_COUNT }, (_, index) => {
    const fraction = index / (TICK_COUNT - 1)
    return {
      index,
      angle: -SWEEP / 2 + fraction * SWEEP,
      active: index <= cursorIndex,
      cursor: index === cursorIndex,
    }
  })
})

// Rotary drag. The dial turns freely rather than mapping to an absolute
// position, so the whole range is reachable without a full sweep of the finger.
let pointerId = null
let lastAngle = 0
let accumulated = 0
let startedAt = 0
let travelled = 0

function angleFrom(event) {
  const rect = knob.value?.getBoundingClientRect()
  if (!rect) return 0
  const cx = rect.left + rect.width / 2
  const cy = rect.top + rect.height / 2
  return (Math.atan2(event.clientY - cy, event.clientX - cx) * 180) / Math.PI
}

function onPointerDown(event) {
  pointerId = event.pointerId
  lastAngle = angleFrom(event)
  accumulated = 0
  travelled = 0
  startedAt = Date.now()

  knob.value?.setPointerCapture?.(pointerId)
  window.addEventListener('pointermove', onPointerMove)
  window.addEventListener('pointerup', onPointerUp)
  window.addEventListener('pointercancel', onPointerUp)
}

function onPointerMove(event) {
  if (event.pointerId !== pointerId) return

  const angle = angleFrom(event)
  let delta = angle - lastAngle

  // atan2 flips sign across the -180/180 boundary; unwrap so a slow drag past
  // 9 o'clock doesn't jump the tempo by the whole range.
  if (delta > 180) delta -= 360
  if (delta < -180) delta += 360

  lastAngle = angle
  accumulated += delta
  travelled += Math.abs(delta)

  const steps = Math.trunc(accumulated / DEGREES_PER_BPM)
  if (steps !== 0) {
    accumulated -= steps * DEGREES_PER_BPM
    const next = Math.min(Math.max(props.modelValue + steps, props.min), props.max)
    if (next !== props.modelValue) emit('update:modelValue', next)
  }
}

function onPointerUp(event) {
  if (pointerId !== null && event.pointerId !== pointerId) return

  const wasTap = travelled * (props.size / 2) * (Math.PI / 180) < TAP_SLOP_PX && Date.now() - startedAt < TAP_MAX_MS
  if (wasTap) emit('tap')

  pointerId = null
  window.removeEventListener('pointermove', onPointerMove)
  window.removeEventListener('pointerup', onPointerUp)
  window.removeEventListener('pointercancel', onPointerUp)
}

onBeforeUnmount(() => {
  window.removeEventListener('pointermove', onPointerMove)
  window.removeEventListener('pointerup', onPointerUp)
  window.removeEventListener('pointercancel', onPointerUp)
})
</script>

<style scoped>
.m7-dial {
  position: relative;
  width: var(--m7-dial-size);
  height: var(--m7-dial-size);
  margin: 0 auto;
  user-select: none;
}

.m7-dial__ring {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.m7-dial__tick {
  stroke: var(--m7-tick);
  stroke-width: 1.6;
  stroke-linecap: round;
  transition:
    stroke 0.12s ease,
    stroke-width 0.12s ease;
}

.m7-dial__tick.is-active {
  stroke: var(--m7-accent);
}

.m7-dial__tick.is-cursor {
  stroke: var(--m7-accent);
  stroke-width: 3.2;
}

.m7-dial__knob {
  position: absolute;
  inset: 15%;
  border: none;
  border-radius: 50%;
  cursor: grab;
  /* The rotary swallows the gesture; without this a drag scrolls the page. */
  touch-action: none;
  background: var(--m7-knob);
  box-shadow:
    0 10px 24px var(--m7-shadow),
    0 2px 4px var(--m7-shadow),
    inset 0 2px 3px var(--m7-knob-highlight),
    inset 0 -3px 6px var(--m7-shadow);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.12s ease;
}

.m7-dial__knob:active {
  cursor: grabbing;
  transform: scale(0.985);
}

.m7-dial__knob:focus-visible {
  outline: 2px solid var(--m7-accent);
  outline-offset: 4px;
}

.m7-dial__tap {
  font-size: 0.7rem;
  letter-spacing: 0.22em;
  font-weight: 400;
  color: var(--m7-muted);
  pointer-events: none;
  transition:
    color 0.15s ease,
    transform 0.15s ease;
}

.m7-dial__tap.is-tapping {
  color: var(--m7-accent);
  transform: scale(1.12);
}

.m7-dial__bound {
  position: absolute;
  bottom: 4%;
  font-size: 0.6rem;
  letter-spacing: 0.14em;
  color: var(--m7-muted);
}

.m7-dial__bound--min {
  left: 12%;
}

.m7-dial__bound--max {
  right: 12%;
}
</style>
